import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { inputClass, labelClass } from '@/components/ui/inputStyles';

export type ForwardCycleFreqKey = 'daily1' | 'every2' | 'every3' | 'custom';
export type ForwardCycleChannel = 'all' | 'moments' | 'groups';
export type ForwardCycleStatus = 'active' | 'paused' | 'completed';

export type ForwardCycleTaskRow = {
  id: number;
  site_id: number;
  miniprogram_id: number;
  title: string;
  hash_link: string;
  promotion_text: string;
  freq_key: ForwardCycleFreqKey;
  custom_interval_days: number;
  freq_label: string;
  planned_executions: number;
  channel: ForwardCycleChannel;
  start_date: string;
  status: ForwardCycleStatus;
  created_at: string;
  moments_done_max?: number;
  groups_done_max?: number;
  robots_progress?: Array<{
    id: number;
    name: string;
    moments_done: number;
    groups_done: number;
    moments_remaining: number;
    groups_remaining: number;
  }>;
};

type Props = {
  open: boolean;
  mpId: number;
  siteId: number;
  editItem?: ForwardCycleTaskRow | null;
  onClose: () => void;
  onSaved: () => void;
  /** 默认管理员接口；站长端传入 site_master_* */
  createAction?: string;
  updateAction?: string;
};

const FREQ_OPTIONS: Array<{ value: ForwardCycleFreqKey; label: string; hint: string }> = [
  { value: 'daily1', label: '每天 1 次', hint: '适合高频曝光' },
  { value: 'every2', label: '每 2 天 1 次', hint: '更稳妥的节奏' },
  { value: 'every3', label: '每 3 天 1 次', hint: '轻量维护曝光' },
  { value: 'custom', label: '自定义', hint: '按你设定的间隔执行' },
];

const CHANNEL_OPTIONS: Array<{ value: ForwardCycleChannel; label: string }> = [
  { value: 'all', label: '发圈 + 群发' },
  { value: 'moments', label: '仅发圈' },
  { value: 'groups', label: '仅群发' },
];

const EXEC_PRESETS = [7, 14, 30, 60] as const;

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function looksLikeHashLink(raw: string): boolean {
  const s = raw.trim();
  return s.includes('#小程序://') || s.startsWith('小程序://') || s.includes('小程序://');
}

export function ForwardCycleTaskModal({
  open,
  mpId,
  siteId,
  editItem = null,
  onClose,
  onSaved,
  createAction = 'create_forward_cycle_task',
  updateAction = 'update_forward_cycle_task',
}: Props) {
  const isEdit = !!editItem && editItem.id > 0;
  const [title, setTitle] = useState('');
  const [hashLink, setHashLink] = useState('');
  const [promotionText, setPromotionText] = useState('');
  const [freqKey, setFreqKey] = useState<ForwardCycleFreqKey>('daily1');
  const [customDays, setCustomDays] = useState(5);
  const [planned, setPlanned] = useState(7);
  const [plannedDraft, setPlannedDraft] = useState('7');
  const [channel, setChannel] = useState<ForwardCycleChannel>('all');
  const [startDate, setStartDate] = useState(todayYmd);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!open) return;
    setMsg('');
    if (editItem) {
      setTitle(editItem.title || '');
      setHashLink(editItem.hash_link || '');
      setPromotionText(editItem.promotion_text || '');
      setFreqKey(editItem.freq_key || 'daily1');
      setCustomDays(Math.max(1, editItem.custom_interval_days || 1));
      setPlanned(Math.max(1, editItem.planned_executions || 7));
      setPlannedDraft(String(Math.max(1, editItem.planned_executions || 7)));
      setChannel(editItem.channel || 'all');
      setStartDate(editItem.start_date || todayYmd());
    } else {
      setTitle('');
      setHashLink('');
      setPromotionText('');
      setFreqKey('daily1');
      setCustomDays(5);
      setPlanned(7);
      setPlannedDraft('7');
      setChannel('all');
      setStartDate(todayYmd());
    }
  }, [open, editItem]);

  const previewLabel = useMemo(() => {
    if (freqKey === 'custom') return `每 ${customDays} 天 1 次 × ${planned} 次`;
    const f = FREQ_OPTIONS.find((o) => o.value === freqKey)?.label ?? '每天 1 次';
    return `${f} × ${planned} 次`;
  }, [freqKey, customDays, planned]);

  const submit = async () => {
    setMsg('');
    const link = hashLink.trim();
    if (!looksLikeHashLink(link)) {
      setMsg('请填写有效的小程序短链接（以 #小程序:// 开头）');
      return;
    }
    const exec = Math.max(1, Math.min(999, parseInt(plannedDraft, 10) || planned));
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        site_id: siteId,
        miniprogram_id: mpId,
        title: title.trim() || '循环推广短链',
        hash_link: link,
        promotion_text: promotionText.trim(),
        freq_key: freqKey,
        custom_interval_days: freqKey === 'custom' ? Math.max(1, Math.min(365, customDays)) : 1,
        planned_executions: exec,
        channel,
        start_date: startDate,
      };
      if (isEdit && editItem) {
        body.id = editItem.id;
        const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>(updateAction, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          setMsg(r.detail || r.err || '保存失败');
          return;
        }
      } else {
        const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>(createAction, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!r.ok) {
          setMsg(r.detail || r.err || '创建失败');
          return;
        }
      }
      onSaved();
      onClose();
    } catch {
      setMsg('网络异常，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? '编辑循环任务' : '添加循环任务'}
      onClose={onClose}
      wide
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">机器人将按频次拉取并推广该短链，逻辑对齐微推广计次。</p>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              取消
            </Button>
            <Button type="button" variant="primary" onClick={() => void submit()} disabled={saving}>
              {saving ? '保存中…' : isEdit ? '保存修改' : '创建任务'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {msg ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p>
        ) : null}

        <div className="rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50/80 to-white px-4 py-3">
          <p className="text-xs font-medium text-teal-800">计划预览</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{previewLabel}</p>
          <p className="mt-0.5 text-xs text-slate-500">每个机器人独立计次；到达执行次数后自动停止。</p>
        </div>

        <label className="block">
          <span className={labelClass}>任务名称（可选）</span>
          <input
            className={inputClass}
            placeholder="例如：首页固定推广"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </label>

        <label className="block">
          <span className={labelClass}>
            小程序短链接 <span className="text-rose-500">*</span>
          </span>
          <textarea
            className={`${inputClass} min-h-[4.5rem] font-mono text-[13px] leading-relaxed`}
            placeholder="#小程序://应用名/页面路径…"
            value={hashLink}
            onChange={(e) => setHashLink(e.target.value)}
          />
          <span className="mt-1 block text-xs text-slate-500">可直接粘贴含短链的文案，系统会自动提取。</span>
        </label>

        <label className="block">
          <span className={labelClass}>推广文案（可选）</span>
          <textarea
            className={`${inputClass} min-h-[5rem]`}
            placeholder="发圈附言；留空则使用「名称 + 短链」"
            value={promotionText}
            onChange={(e) => setPromotionText(e.target.value)}
          />
        </label>

        <div>
          <span className={labelClass}>循环频次</span>
          <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {FREQ_OPTIONS.map((opt) => {
              const active = freqKey === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFreqKey(opt.value)}
                  className={
                    active
                      ? 'rounded-xl border border-teal-300 bg-teal-50 px-3 py-2.5 text-left shadow-sm ring-1 ring-teal-200/80'
                      : 'rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left hover:border-slate-300 hover:bg-slate-50'
                  }
                >
                  <div className={`text-sm font-semibold ${active ? 'text-teal-900' : 'text-slate-800'}`}>
                    {opt.label}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{opt.hint}</div>
                </button>
              );
            })}
          </div>
          {freqKey === 'custom' ? (
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <span>每隔</span>
              <input
                type="number"
                min={1}
                max={365}
                className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm"
                value={customDays}
                onChange={(e) => setCustomDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 1)))}
              />
              <span>天执行 1 次</span>
            </label>
          ) : null}
        </div>

        <div>
          <span className={labelClass}>
            执行次数 <span className="text-rose-500">*</span>
          </span>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {EXEC_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setPlanned(n);
                  setPlannedDraft(String(n));
                }}
                className={
                  planned === n
                    ? 'rounded-lg border border-teal-300 bg-teal-50 px-3 py-1.5 text-sm font-medium text-teal-900'
                    : 'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50'
                }
              >
                {n} 次
              </button>
            ))}
            <input
              type="number"
              min={1}
              max={999}
              className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              value={plannedDraft}
              onChange={(e) => {
                setPlannedDraft(e.target.value);
                const n = parseInt(e.target.value, 10);
                if (Number.isFinite(n) && n >= 1) setPlanned(Math.min(999, n));
              }}
            />
          </div>
        </div>

        <div>
          <span className={labelClass}>推广渠道</span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {CHANNEL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setChannel(opt.value)}
                className={
                  channel === opt.value
                    ? 'rounded-lg border border-teal-300 bg-teal-50 px-3 py-1.5 text-sm font-medium text-teal-900'
                    : 'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50'
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <label className="block max-w-xs">
          <span className={labelClass}>开始日期</span>
          <input
            type="date"
            className={inputClass}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
}
